import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import SEO from "@/components/SEO";
import { CATEGORIES } from "@/data/products";
import { useProducts, getCategoryLabel } from "@/lib/useProducts";
import { useCart } from "@/lib/CartContext";
import { formatPrice, priceForSize, startingPrice } from "@/lib/brand";
import { KITS } from "@/data/kits";
import { toast } from "sonner";

const Products = () => {
  const [filter, setFilter] = useState("all");
  const { products } = useProducts();
  const { addItem } = useCart();

  const items = useMemo(() => {
    if (filter === "all") return products;
    if (filter === "kits") return [];
    return products.filter((p) => p.category === filter);
  }, [filter, products]);

  const quickAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    const firstSize = (p.sizes && p.sizes[0]) || "";
    addItem({
      slug: p.slug,
      name: p.name,
      size: firstSize,
      qty: 1,
      price: formatPrice(priceForSize(p, firstSize)),
      image: p.image,
    });
    toast.success(`${p.name} added to basket`);
  };

  const hasVariants = (p) => p.variant_prices && Object.keys(p.variant_prices).length > 1;

  return (
    <div data-testid="products-page">
      <SEO
        title="Buy Puja Essentials Online — Sanatan Dharma"
        description="Shop 24 authentic puja essentials from Sanatan Dharma — kapoor, kumkum, kalava, gangajal, sambrani cups, agarbatti, dhoop, havan samagri and complete pooja kits. Premium quality, trusted brand, pan-India delivery."
        keywords="buy puja essentials online, buy kapoor online, buy kumkum online, buy kalava online, buy sambrani cups, buy gangajal, buy havan samagri, buy agarbatti online, buy dhoop online, puja items online india, sanatan dharma products, har ghar ka vishwas"
        path="/products"
      />
      <section className="mandala-bg border-b border-[#3B2A1A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHeading
            overline="The Catalogue"
            title="Every Essential, Held to One Standard."
            subtitle="From the simplest kapoor tablet to the most considered havan samagri — a complete catalogue for the modern Indian home."
          />
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3 border-b border-[#3B2A1A]/10 pb-6" data-testid="product-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                data-testid={`filter-${c.id}`}
                className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] font-semibold border transition-colors ${
                  filter === c.id
                    ? "bg-[#3B2A1A] text-[#FBF6EC] border-[#3B2A1A]"
                    : "border-[#3B2A1A]/20 text-[#3B2A1A] hover:border-[#C89D3C] hover:text-[#C89D3C]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {filter === "kits" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="kits-grid">
              {KITS.map((k) => (
                <Link
                  key={k.slug}
                  to="/kits"
                  data-testid={`product-kit-${k.slug}`}
                  className="relative min-h-[320px] p-8 text-white flex flex-col justify-between overflow-hidden group"
                  style={{ background: k.color }}
                >
                  <div className="absolute inset-0 mandala-bg opacity-20" />
                  <div className="relative">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-white/70">Puja Kit</p>
                    <h3 className="font-serif-display text-3xl mt-2">{k.name}</h3>
                    <p className="text-sm text-white/85 mt-2">{k.tagline}</p>
                  </div>
                  <span className="relative text-xs uppercase tracking-[0.2em] font-semibold">Explore →</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[#3B2A1A]/10 mt-10 border border-[#3B2A1A]/10" data-testid="products-grid">
              {items.map((p) => (
                <Link
                  to={`/products/${p.slug}`}
                  key={p.slug}
                  data-testid={`product-card-${p.slug}`}
                  className="group bg-white p-5 sm:p-7 flex flex-col hover:bg-[#FBF6EC]/40 transition-colors"
                >
                  <div className="aspect-square overflow-hidden bg-[#F5EFE3] mb-5">
                    <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  </div>
                  <p className="overline text-[10px]">{getCategoryLabel(p.category)}</p>
                  <h3 className="font-serif-display text-2xl text-[#3B2A1A] mt-2">{p.name}</h3>
                  <p className="text-sm text-[#8A7969] mt-1 font-light">{p.tagline}</p>
                  {p.sizes && p.sizes.length > 0 && (
                    <p className="text-[11px] text-[#5C4A3A] mt-3 font-light">
                      <span className="uppercase tracking-[0.18em] text-[#8A7969] text-[9px] font-semibold mr-2">Sizes</span>
                      {p.sizes.join(" · ")}
                    </p>
                  )}
                  {p.packaging && (
                    <p className="text-[11px] text-[#5C4A3A] mt-1 font-light">
                      <span className="uppercase tracking-[0.18em] text-[#8A7969] text-[9px] font-semibold mr-2">Pack</span>
                      {p.packaging}
                    </p>
                  )}
                  {formatPrice(startingPrice(p)) && (
                    <p className="mt-3 font-serif-display text-lg text-[#3B2A1A]" data-testid={`price-${p.slug}`}>
                      {hasVariants(p) && <span className="text-[9px] uppercase tracking-[0.2em] text-[#8A7969] font-semibold mr-1.5">From</span>}
                      {formatPrice(startingPrice(p))}
                    </p>
                  )}
                  <div className="mt-auto pt-5 flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C89D3C] flex items-center gap-2">
                      Explore <ArrowRight size={12} />
                    </span>
                    <button
                      onClick={(e) => quickAdd(e, p)}
                      data-testid={`quick-add-${p.slug}`}
                      aria-label={`Add ${p.name} to cart`}
                      className="p-2 border border-[#3B2A1A]/20 text-[#3B2A1A] hover:bg-[#3B2A1A] hover:text-[#FBF6EC] transition-colors"
                    >
                      <ShoppingBag size={14} />
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
